import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, Clock, User, Mail, Phone, CreditCard, ArrowLeft, CheckCircle, Calculator, MapPin, HelpCircle, AlertCircle, Upload, FileText, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from './apiConfig';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { trackEvent } from './lib/analytics';

const EventBooking = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Event Details, 2: Calendar, 3: Time Slots, 4: Contact, 5: T&C, 6: Success
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    phone: '',
    event_type: '',
    guest_count: '',
    decor_size: '',
    special_requirements: ''
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [checkingDates, setCheckingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);

  // Enhanced validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Validation functions
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    switch (name) {
      case 'user_name':
        if (!value.trim()) {
          errors[name] = 'Name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          errors[name] = 'Name can only contain letters and spaces';
        } else {
          delete errors[name];
        }
        break;

      case 'user_email':
        if (!value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors[name] = 'Please enter a valid email address';
        } else {
          delete errors[name];
        }
        break;

      case 'phone':
        if (!value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (!/^[6-9]\d{9}$/.test(value.replace(/\D/g, ''))) {
          errors[name] = 'Please enter a valid 10-digit Indian phone number';
        } else {
          delete errors[name];
        }
        break;

      case 'event_type':
        if (!value.trim()) {
          errors[name] = 'Please select an event type';
        } else {
          delete errors[name];
        }
        break;

      case 'guest_count':
        const guestNum = parseInt(value);
        if (!value.trim()) {
          errors[name] = 'Guest count is required';
        } else if (isNaN(guestNum) || guestNum < 1) {
          errors[name] = 'Please enter a valid number of guests';
        } else if (guestNum > 500) {
          errors[name] = 'Maximum 500 guests allowed';
        } else {
          delete errors[name];
        }
        break;

      case 'decor_size':
        if (!value.trim()) {
          errors[name] = 'Please select a decor size';
        } else {
          delete errors[name];
        }
        break;

      case 'special_requirements':
        if (value.trim().length > 500) {
          errors[name] = 'Special requirements cannot exceed 500 characters';
        } else {
          delete errors[name];
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const fields = ['user_name', 'user_email', 'phone', 'event_type', 'guest_count', 'decor_size'];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    if (!acceptedTerms) {
      setValidationErrors(prev => ({ ...prev, terms: 'You must accept the terms and conditions' }));
      isValid = false;
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.terms;
        return newErrors;
      });
    }

    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // Real-time validation
    if (touchedFields[name]) {
      validateField(name, value);
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // Handle ID file upload
  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIdFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setIdPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setIdPreview('pdf');
    }
  };

  const eventTypes = [
    'Birthday Party',
    'Anniversary',
    'Corporate Event',
    'Wedding Reception',
    'Baby Shower',
    'Graduation Party',
    'Holiday Celebration',
    'Other'
  ];

  const [settings, setSettings] = useState(null);
  const [decorSizes, setDecorSizes] = useState([
    { value: 'small', label: 'Small', multiplier: 1.0 },
    { value: 'medium', label: 'Medium', multiplier: 1.3 },
    { value: 'large', label: 'Large', multiplier: 1.6 }
  ]);
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    axios.get('/api/admin/settings', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (res.data.success) {
          setSettings(res.data.settings.event_pricing);
          const m = res.data.settings.event_pricing?.decorMultipliers || {};
          setDecorSizes([
            { value: 'small', label: 'Small', multiplier: Number(m.small ?? 1) },
            { value: 'medium', label: 'Medium', multiplier: Number(m.medium ?? 1.3) },
            { value: 'large', label: 'Large', multiplier: Number(m.large ?? 1.6) }
          ]);
        }
      })
      .catch(() => {
        // Keep defaults if settings not available
      });
  }, []);

  const timeSlotOptions = [
    { value: '08:00', label: '08:00 AM' },
    { value: '09:00', label: '09:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '13:00', label: '01:00 PM' },
    { value: '14:00', label: '02:00 PM' },
    { value: '15:00', label: '03:00 PM' },
    { value: '16:00', label: '04:00 PM' },
    { value: '17:00', label: '05:00 PM' },
    { value: '18:00', label: '06:00 PM' },
    { value: '19:00', label: '07:00 PM' }
  ];

  // Auto-quote calculation
  const calculateQuote = () => {
    if (!formData.event_type || !formData.guest_count || !formData.decor_size) {
      setQuote(null);
      return;
    }

    const guestCount = parseInt(formData.guest_count);
    const decorOption = decorSizes.find(d => d.value === formData.decor_size);

    if (!decorOption) return;

    // Base pricing logic (from settings with fallbacks)
    const cfg = settings || { basePrice: 5000, perGuestPrice: 100, housekeepingFee: 1000, deposit: 2500 };
    let basePrice = Number(cfg.basePrice) || 5000;
    let perGuestPrice = guestCount * (Number(cfg.perGuestPrice) || 100);
    let decorPrice = basePrice * ((decorOption.multiplier || 1) - 1);
    let housekeepingFee = Number(cfg.housekeepingFee) || 1000;
    let deposit = Number(cfg.deposit) || 2500;

    const subtotal = basePrice + perGuestPrice + decorPrice;
    const total = subtotal + housekeepingFee + deposit;

    setQuote({
      basePrice,
      perGuestPrice,
      decorPrice,
      housekeepingFee,
      deposit,
      subtotal,
      total
    });
  };

  useEffect(() => {
    calculateQuote();
  }, [formData.event_type, formData.guest_count, formData.decor_size]);

  // Check booked dates for the month
  const checkAvailability = async () => {
    setCheckingDates(true);
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0); // Check current + next month

      // Generate all dates in range to check
      const dates = [];
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const checkDate = new Date(d);
        checkDate.setHours(10, 0, 0, 0); // Use 10 AM as reference time
        dates.push(checkDate.toISOString());
      }

      const response = await fetch(`${API_BASE_URL}/api/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration: 8, // Events are typically 8 hours
          slots: dates.map(date => ({ slot_time: date, duration: 8 }))
        })
      });

      const result = await response.json();
      if (result.success) {
        const booked = result.available_slots
          .filter(slot => !slot.available)
          .map(slot => new Date(slot.slot_time).toISOString().split('T')[0]);
        setBookedDates(booked);
      }
    } catch (error) {
      console.error('Error checking dates:', error);
      toast.error('Failed to load calendar availability');
    } finally {
      setCheckingDates(false);
    }
  };

  // Load available time slots for selected date
  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setLoadingSlots(true);

    try {
      // Check all time slots for this date
      const slotsToCheck = timeSlotOptions.map(timeOption => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const slot_time = moment(`${dateStr} ${timeOption.value}`, 'YYYY-MM-DD HH:mm')
          .utcOffset(330, true)
          .toISOString();

        return {
          slot_time: slot_time,
          duration: 8, // Events are 8 hours
          option: timeOption
        };
      });

      const response = await fetch(`${API_BASE_URL}/api/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration: 8,
          slots: slotsToCheck.map(s => ({ slot_time: s.slot_time, duration: s.duration }))
        })
      });

      const result = await response.json();
      if (result.success) {
        const available = slotsToCheck.map((slot, index) => ({
          ...slot.option,
          available: result.available_slots[index]?.available || false,
          reason: result.available_slots[index]?.reason
        }));
        setAvailableTimeSlots(available);
        setCurrentStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      toast.error('Failed to load available time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const proceedToCalendar = () => {
    if (!formData.event_type || !formData.guest_count || !formData.decor_size) {
      toast.error('Please fill in all event details');
      return;
    }
    if (!quote) {
      toast.error('Quote calculation failed');
      return;
    }
    setCurrentStep(2);
    checkAvailability();
  };

  const isDateDisabled = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookedDates.includes(dateStr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      // Upload ID Proof first
      let idProofUrl = null;
      let idProofExpiresAt = null;

      if (idFile) {
        setUploadingId(true);
        const formDataId = new FormData();
        formDataId.append('idProof', idFile);

        const idUploadResponse = await fetch(`${API_BASE_URL}/api/upload-id`, {
          method: 'POST',
          body: formDataId
        });

        const idResult = await idUploadResponse.json();

        if (idResult.success) {
          idProofUrl = idResult.url;
          idProofExpiresAt = idResult.expiresAt;
        } else {
          toast.error('Failed to upload ID. Please try again.');
          setLoading(false);
          return;
        }
        setUploadingId(false);
      } else {
        toast.error('Please upload an ID proof');
        setLoading(false);
        return;
      }

      // Create slot_time from selected date and time
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const slot_time = moment(`${dateStr} ${selectedTime.value}`, 'YYYY-MM-DD HH:mm')
        .utcOffset(330, true)
        .toISOString();

      const bookingData = {
        user_name: formData.user_name,
        user_email: formData.user_email,
        phone: formData.phone,
        booking_type: 'event',
        duration: '8hr_event',
        duration_label: '8 Hours Event',
        guest_use_time: '8 hours',
        buffer_after: '1 hour',
        slot_time: slot_time,
        event_type: formData.event_type,
        guest_count: parseInt(formData.guest_count),
        decor_size: formData.decor_size,
        special_requirements: formData.special_requirements,
        amount: quote.subtotal,
        housekeeping_fee: quote.housekeepingFee,
        deposit: quote.deposit,
        deposit: quote.deposit,
        total_amount: quote.total,
        id_proof_url: idProofUrl,
        id_proof_expires_at: idProofExpiresAt
      };

      trackEvent('begin_checkout', {
        currency: 'INR',
        value: bookingData.total_amount,
        item_id: 'event_booking',
      });
      const response = await fetch(`${API_BASE_URL}/api/create-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      const result = await response.json();

      if (result.success) {
        trackEvent('booking_completed', {
          currency: 'INR',
          value: bookingData.total_amount,
          item_id: 'event_booking',
        });
        toast.success('Booking confirmed successfully!');
        setCurrentStep(6);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        trackEvent('booking_failed', { item_id: 'event_booking', reason: result.message || 'unknown' });
        toast.error(result.message || 'Failed to create booking');
      }
    } catch (error) {
      trackEvent('booking_failed', { item_id: 'event_booking', reason: 'request_error' });
      console.error('Booking error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Event Details';
      case 2: return 'Select Date';
      case 3: return 'Select Time';
      case 4: return 'Contact Information';
      case 5: return 'Terms & Conditions';
      case 6: return 'Booking Confirmed';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#F4ECDE' }}>
      {/* Maintenance Banner */}
      {new Date() < new Date('2026-07-26') && (
        <div className="bg-amber-50 border-b border-amber-200 -mx-4 mb-8 px-4 py-2.5 flex items-center justify-center gap-2.5 text-center">
          <span className="text-amber-600 text-lg leading-none">🔧</span>
          <p className="text-sm font-medium text-amber-800">
            Maintenance Break — <span className="font-semibold">July 1st – July 25th</span>. Bookings for these dates are unavailable.
          </p>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              currentStep === 1 ? navigate('/') : setCurrentStep(currentStep - 1);
            }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <h1 className="text-3xl font-bold" style={{ color: '#471F21' }}>
            Event Booking
          </h1>
          <div className="w-20"></div>
        </div>

        {/* Progress Steps */}
        {currentStep < 6 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep >= step
                    ? 'text-white'
                    : 'bg-gray-200 text-gray-500'
                    }`} style={currentStep >= step ? { backgroundColor: '#471F21' } : {}}>
                    {step}
                  </div>
                  {step < 5 && (
                    <div className={`h-1 w-12 mx-2 ${currentStep > step ? 'bg-gray-400' : 'bg-gray-200'
                      }`} style={currentStep > step ? { backgroundColor: '#471F21' } : {}}></div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm font-medium mt-2" style={{ color: '#471F21' }}>
              {getStepTitle()}
            </p>
          </div>
        )}

        {/* Step 1: Event Details with Auto-Quote */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  Event Type *
                </label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  style={{ focusRing: '#471F21' }}
                >
                  <option value="">Select event type</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  Number of Guests *
                </label>
                <input
                  type="number"
                  name="guest_count"
                  value={formData.guest_count}
                  onChange={handleChange}
                  required
                  min="1"
                  max="100"
                  placeholder="Enter number of guests"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  Decoration Size *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {decorSizes.map(decor => (
                    <button
                      key={decor.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, decor_size: decor.value }))}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${formData.decor_size === decor.value
                        ? 'border-current shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      style={formData.decor_size === decor.value ? { borderColor: '#471F21', backgroundColor: '#FFF5E6' } : {}}
                    >
                      <div className="font-semibold" style={{ color: '#471F21' }}>{decor.label}</div>
                      <div className="text-sm text-gray-600">{decor.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  Special Requirements
                </label>
                <textarea
                  name="special_requirements"
                  value={formData.special_requirements}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any special requests or requirements..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                />
              </div>

              {/* Auto-Generated Quote */}
              {quote && (
                <div className="rounded-lg p-6 space-y-3" style={{ backgroundColor: '#FFF5E6', border: '2px solid #471F21' }}>
                  <div className="flex items-center mb-3">
                    <Calculator className="w-5 h-5 mr-2" style={{ color: '#471F21' }} />
                    <h3 className="font-bold text-lg" style={{ color: '#471F21' }}>
                      Instant Quote
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="border-t-2 pt-2 mt-2" style={{ borderColor: '#471F21' }}>
                      <div className="flex justify-between items-end">
                        <div className="flex flex-col">
                          <span className="font-bold text-lg" style={{ color: '#471F21' }}>Total Amount:</span>
                          <span className="text-xs text-gray-500 italic mt-0.5">
                            (Inclusive of venue, guest charges, decor, housekeeping & deposit)
                          </span>
                        </div>
                        <span className="font-bold text-xl" style={{ color: '#471F21' }}>₹{quote.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={proceedToCalendar}
                disabled={!quote}
                className="w-full text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                style={{ backgroundColor: '#471F21' }}
              >
                Continue to Date Selection →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Calendar Selection */}
        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <Calendar className="w-12 h-12 mx-auto mb-3" style={{ color: '#471F21' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#471F21' }}>
                Select Your Event Date
              </h2>
              <p className="text-gray-600">
                Choose a date for your {formData.event_type}
              </p>
            </div>

            {checkingDates ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#471F21' }}></div>
                <p className="mt-4 text-gray-600">Loading availability...</p>
              </div>
            ) : (
              <div className="flex justify-center">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateSelect}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)}
                  filterDate={(date) => !isDateDisabled(date)}
                  inline
                  disabledKeyboardNavigation
                  calendarClassName="custom-calendar"
                />
              </div>
            )}

            {loadingSlots && (
              <div className="text-center mt-6">
                <p className="text-gray-600">Loading available time slots...</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Time Slot Selection */}
        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <Clock className="w-12 h-12 mx-auto mb-3" style={{ color: '#471F21' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#471F21' }}>
                Available Time Slots
              </h2>
              <p className="text-gray-600">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {availableTimeSlots.map(slot => (
                <button
                  key={slot.value}
                  onClick={() => slot.available && setSelectedTime(slot)}
                  disabled={!slot.available}
                  className={`p-4 border-2 rounded-lg font-medium transition-all ${selectedTime?.value === slot.value
                    ? 'border-current shadow-md'
                    : slot.available
                      ? 'border-gray-200 hover:border-gray-300'
                      : 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                    }`}
                  style={selectedTime?.value === slot.value ? { borderColor: '#471F21', backgroundColor: '#FFF5E6', color: '#471F21' } : {}}
                >
                  <Clock className="w-5 h-5 mx-auto mb-2" />
                  {slot.label}
                  {!slot.available && (
                    <div className="text-xs text-red-600 mt-1">Unavailable</div>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                setCurrentStep(4);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={!selectedTime}
              className="w-full text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
              style={{ backgroundColor: '#471F21' }}
            >
              Continue to Contact Details →
            </button>
          </div>
        )}

        {/* Step 4: Contact Details */}
        {currentStep === 4 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <User className="w-12 h-12 mx-auto mb-3" style={{ color: '#471F21' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#471F21' }}>
                Contact Information
              </h2>
              <p className="text-gray-600">We'll send your booking confirmation here</p>
            </div>

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  <User className="w-4 h-4 mr-1" />
                  Full Name *
                  <div className="relative ml-2 group">
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                      Enter your full legal name as it appears on official documents
                    </div>
                  </div>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="Enter your full name"
                    className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:outline-none transition-all ${touchedFields.user_name
                      ? validationErrors.user_name
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-green-500 focus:ring-green-200'
                      : 'border-gray-300 focus:ring-blue-200'
                      }`}
                  />
                  {touchedFields.user_name && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {validationErrors.user_name ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {touchedFields.user_name && validationErrors.user_name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.user_name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  <Mail className="w-4 h-4 mr-1" />
                  Email Address *
                  <div className="relative ml-2 group">
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                      We'll send your booking confirmation to this email
                    </div>
                  </div>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="user_email"
                    value={formData.user_email}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="your.email@example.com"
                    className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:outline-none transition-all ${touchedFields.user_email
                      ? validationErrors.user_email
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-green-500 focus:ring-green-200'
                      : 'border-gray-300 focus:ring-blue-200'
                      }`}
                  />
                  {touchedFields.user_email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {validationErrors.user_email ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {touchedFields.user_email && validationErrors.user_email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.user_email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                  <Phone className="w-4 h-4 mr-1" />
                  WhatsApp Phone number *
                  <div className="relative ml-2 group">
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                      Enter a valid 10-digit Indian mobile number
                    </div>
                  </div>
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="10-digit mobile number"
                    className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:outline-none transition-all ${touchedFields.phone
                      ? validationErrors.phone
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-green-500 focus:ring-green-200'
                      : 'border-gray-300 focus:ring-blue-200'
                      }`}
                  />
                  {touchedFields.phone && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {validationErrors.phone ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {touchedFields.phone && validationErrors.phone && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.phone}
                  </p>
                )}
              </div>

              {/* ID Proof Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Upload ID Proof *
                  <span className="text-xs text-gray-500 ml-2">(Aadhaar / Driving License / Voter ID)</span>
                </label>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#471F21] transition-colors bg-gray-50">
                  {idFile ? (
                    <div className="flex items-center justify-between bg-white p-3 rounded border border-gray-200">
                      <div className="flex items-center">
                        {idPreview === 'pdf' ? (
                          <FileText className="w-8 h-8 text-red-500 mr-3" />
                        ) : (
                          <img src={idPreview} alt="ID Preview" className="w-10 h-10 object-cover rounded mr-3" />
                        )}
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{idFile.name}</p>
                          <p className="text-xs text-gray-500">{(idFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setIdFile(null); setIdPreview(null); }}
                        className="p-1 hover:bg-gray-100 rounded-full text-gray-500 hover:text-red-500"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload your ID proof</p>
                      <p className="text-xs text-gray-400">JPG, PNG, or PDF (Max 5MB)</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleIdUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style={{ height: '100px', marginTop: '-100px' }} // Overlay on the div
                      />
                      {/* Fix for file input overlay being tricky */}
                      <div className="relative mt-4">
                        <input
                          type="file"
                          id="id-upload"
                          accept="image/jpeg,image/png,application/pdf"
                          onChange={handleIdUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="id-upload"
                          className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none cursor-pointer"
                        >
                          Select File
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button
                onClick={() => {
                  setCurrentStep(5);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={!formData.user_name || !formData.user_email || !formData.phone || !idFile}
                className="w-full text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                style={{ backgroundColor: '#471F21' }}
              >
                Continue to Terms & Conditions →
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Terms & Conditions */}
        {currentStep === 5 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#471F21' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#471F21' }}>
                Review & Confirm
              </h2>
            </div>

            {/* Booking Summary */}
            <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: '#FFF5E6', border: '1px solid #471F21' }}>
              <h3 className="font-bold text-lg mb-4" style={{ color: '#471F21' }}>Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Event Type:</span>
                  <span>{formData.event_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date:</span>
                  <span>{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Time:</span>
                  <span>{selectedTime?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Guests:</span>
                  <span>{formData.guest_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Decoration:</span>
                  <span className="capitalize">{formData.decor_size}</span>
                </div>
                <div className="border-t-2 pt-2 mt-2" style={{ borderColor: '#471F21' }}>
                  <div className="flex justify-between font-bold text-lg" style={{ color: '#471F21' }}>
                    <span>Total Amount:</span>
                    <span>₹{quote?.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6 max-h-64 overflow-y-auto">
              <h3 className="font-bold mb-3" style={{ color: '#471F21' }}>Terms & Conditions</h3>
              <div className="text-sm text-gray-700 space-y-2">
                <div className="space-y-4">
                  <div>
                    <h5 className="font-bold text-[#471F21] text-xs uppercase mb-1">Rescheduling Policy</h5>
                    <ul className="list-disc pl-4 text-xs space-y-0.5">
                      <li>Rescheduling requests are <strong>not permitted within 7 days</strong> of the event date.</li>
                      <li>In exceptional emergencies, subject to availability, one reschedule may be allowed if requested via email/WhatsApp and completed within one month.</li>
                      <li>Requests within 7 days are treated as new bookings (non-transferable payment).</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-bold text-[#471F21] text-xs uppercase mb-1">Know Before You Proceed</h5>
                    <ul className="list-disc pl-4 text-xs space-y-0.5">
                      <li>Thaathayya Illu is a private home. Please respect the space.</li>
                      <li><strong>Time slot is fixed</strong> (gate-to-gate). Arrive 10-15 mins early.</li>
                      <li>Additional time charged at <strong>₹1,800/hr</strong> (subject to availability).</li>
                      <li>Do not drag furniture. Ask caretaker for help.</li>
                      <li>Roof climbing is strictly prohibited.</li>
                      <li>Damage/tampering attracts min. fine of <strong>₹2,500</strong>.</li>
                      <li>Alcohol and smoking are strictly prohibited.</li>
                      <li>Maximum guest count must not exceed booked capacity.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 mr-3 h-5 w-5 rounded"
                  style={{ accentColor: '#471F21' }}
                />
                <span className="text-sm">
                  I have read and agree to the terms and conditions, cancellation policy, and house rules.
                </span>
              </label>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!acceptedTerms || loading}
              className="w-full text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
              style={{ backgroundColor: '#471F21' }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirm Booking (₹{quote?.total.toLocaleString()})
                </span>
              )}
            </button>
          </div>
        )}

        {/* Step 6: Success */}
        {currentStep === 6 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-500" />
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#471F21' }}>
              Booking Confirmed!
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Your event booking has been confirmed. You'll receive a confirmation email shortly.
            </p>
            <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: '#FFF5E6' }}>
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="font-medium">Event:</span>
                  <span>{formData.event_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date:</span>
                  <span>{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Time:</span>
                  <span>{selectedTime?.label}</span>
                </div>
                <div className="flex justify-between font-bold" style={{ color: '#471F21' }}>
                  <span>Total Paid:</span>
                  <span>₹{quote?.total.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                navigate('/');
              }}
              className="w-full text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all hover:shadow-lg"
              style={{ backgroundColor: '#471F21' }}
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBooking;
